import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../cn';
import { Button } from './forms';

function useLockBody(open: boolean) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);
}

/** Bottom sheet — the standard way to pick / edit without leaving the screen. */
export function Sheet({ open, onClose, title, children, footer, tall }: { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; footer?: ReactNode; tall?: boolean }) {
  useLockBody(open);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center md:p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 animate-[dd-fade-in_0.15s_ease-out] bg-neutral-900/50" onClick={onClose} />
      <div className={cn('relative flex w-full max-w-xl animate-[dd-sheet-in_0.22s_ease-out] flex-col rounded-t-3xl bg-white shadow-float md:rounded-3xl', tall ? 'h-[92dvh] md:h-[min(86dvh,820px)] md:max-w-2xl' : 'max-h-[88dvh] md:max-h-[86dvh] md:max-w-lg')}>
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-neutral-300 md:hidden" />
        {title !== undefined && (
          <div className="flex items-center justify-between px-5 pb-2 pt-3">
            <h2 className="text-lg font-bold text-ink">{title}</h2>
            <button type="button" onClick={onClose} aria-label="বন্ধ" className="flex h-9 w-9 items-center justify-center rounded-full text-ink-3 hover:bg-neutral-100"><X size={20} /></button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer && <div className="safe-bottom border-t border-line bg-white px-5 pt-3 pb-3.5">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Dialog({ open, onClose, title, children, actions }: { open: boolean; onClose: () => void; title: ReactNode; children?: ReactNode; actions: ReactNode }) {
  useLockBody(open);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" role="alertdialog" aria-modal="true">
      <div className="absolute inset-0 animate-[dd-fade-in_0.15s_ease-out] bg-neutral-900/55" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-float">
        <h2 className="text-lg font-bold text-ink">{title}</h2>
        {children && <div className="mt-2 text-[15px] leading-relaxed text-ink-2">{children}</div>}
        <div className="mt-5 flex justify-end gap-2">{actions}</div>
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({ open, title, body, confirmLabel, cancelLabel, danger, onConfirm, onClose, loading }: { open: boolean; title: string; body?: ReactNode; confirmLabel: string; cancelLabel: string; danger?: boolean; onConfirm: () => void; onClose: () => void; loading?: boolean }) {
  return (
    <Dialog open={open} onClose={onClose} title={title} actions={<><Button variant="ghost" onClick={onClose}>{cancelLabel}</Button><Button variant={danger ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>{confirmLabel}</Button></>}>
      {body}
    </Dialog>
  );
}

// ── toast ────────────────────────────────────────────────────────────────────
interface ToastItem { id: number; text: string; tone: 'ok' | 'bad' | 'info' }
let items: ToastItem[] = [];
let nextId = 1;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());
export function toast(text: string, tone: ToastItem['tone'] = 'info', ms = 3200): void {
  const id = nextId++;
  items = [...items, { id, text, tone }];
  emit();
  setTimeout(() => { items = items.filter((t) => t.id !== id); emit(); }, ms);
}
export const toastOk = (t: string) => toast(t, 'ok');
export const toastErr = (t: string) => toast(t, 'bad', 4500);

export function Toaster() {
  const list = useSyncExternalStore((cb) => { subs.add(cb); return () => subs.delete(cb); }, () => items);
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-24 z-[70] flex flex-col items-center gap-2 px-4" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={cn('animate-[dd-toast-in_0.2s_ease-out] max-w-sm rounded-xl px-4 py-2.5 text-sm font-medium text-white shadow-float', t.tone === 'ok' ? 'bg-ok' : t.tone === 'bad' ? 'bg-bad' : 'bg-neutral-800')}>{t.text}</div>
      ))}
    </div>,
    document.body,
  );
}

/** Popover anchored to a button, closes on outside tap. */
export function Popover({ open, onClose, children, className }: { open: boolean; onClose: () => void; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent | TouchEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('touchstart', h); };
  }, [open, onClose]);
  if (!open) return null;
  return <div ref={ref} className={cn('absolute z-40 min-w-56 rounded-2xl border border-line bg-white p-1.5 shadow-float', className)}>{children}</div>;
}

export function useDisclosure(initial = false): [boolean, () => void, () => void] {
  const [o, set] = useState(initial);
  return [o, () => set(true), () => set(false)];
}
